import React, { useEffect, useMemo, useState } from "react";
import { fetchBundleCatalogIds, fetchBundleCatalogLabels, searchBundleCatalog } from "../api/MerchandisingAPIs";
import { resolveMediaUrl } from "./Catalog/catalogUi";

export const emptyDimension = () => ({
  mode: "None",
  includeIds: [],
  excludeIds: [],
  categoryIds: [],
  brandIds: [],
});

export const emptySelection = () => ({
  designs: emptyDimension(),
  products: emptyDimension(),
  variants: emptyDimension(),
  phoneModels: emptyDimension(),
  caseStyles: emptyDimension(),
  touched: true,
});

const asIds = (value) => (Array.isArray(value) ? value.map(Number).filter((id) => id > 0) : []);

function fromLegacy(includeIds, ruleIds, ruleKey) {
  const includes = asIds(includeIds);
  const rules = asIds(ruleIds);
  if (!includes.length && !rules.length) return emptyDimension();
  if (rules.length && !includes.length) {
    return {
      mode: "Rules",
      includeIds: [],
      excludeIds: [],
      categoryIds: ruleKey === "category" ? rules : [],
      brandIds: ruleKey === "brand" ? rules : [],
    };
  }
  return { mode: "Manual", includeIds: includes, excludeIds: [], categoryIds: [], brandIds: [] };
}

export function selectionFromSlot(slot) {
  if (slot?.selection) {
    return {
      designs: { ...emptyDimension(), ...(slot.selection.designs || {}) },
      products: { ...emptyDimension(), ...(slot.selection.products || {}) },
      variants: { ...emptyDimension(), ...(slot.selection.variants || {}) },
      phoneModels: { ...emptyDimension(), ...(slot.selection.phoneModels || {}) },
      caseStyles: { ...emptyDimension(), ...(slot.selection.caseStyles || {}) },
      touched: true,
    };
  }
  return {
    designs: fromLegacy(slot?.allowedDesignIds, slot?.allowedCategoryIds, "category"),
    products: fromLegacy(slot?.allowedProductIds, [], null),
    variants: fromLegacy(slot?.allowedVariantIds, [], null),
    phoneModels: fromLegacy(slot?.allowedTypeIds, [], null),
    caseStyles: fromLegacy(slot?.allowedCaseStyleIds, [], null),
    touched: false,
  };
}

const MODE_LABELS = {
  None: "أي حاجة",
  All: "كل المتاح والجديد",
  Manual: "اختار بنفسك",
};

function summary(dimension, noun, ruleKind) {
  if (!dimension || dimension.mode === "None") return `أي ${noun} متاحة`;
  if (dimension.mode === "All") {
    const excluded = dimension.excludeIds?.length || 0;
    return excluded ? `كل ${noun} المتاحة ما عدا ${excluded}` : `كل ${noun} المتاحة، واللي يتضاف بعد كده`;
  }
  if (dimension.mode === "Rules") {
    const groups = (dimension.categoryIds?.length || 0) + (dimension.brandIds?.length || 0);
    const extra = dimension.includeIds?.length || 0;
    const excluded = dimension.excludeIds?.length || 0;
    const groupName = ruleKind === "brand" ? "ماركة" : "فئة";
    return `${groups} ${groupName}${extra ? ` + ${extra} قطعة زيادة` : ""}${excluded ? ` ما عدا ${excluded}` : ""}. اللي يتضاف ويطابق الشرط يدخل لوحده`;
  }
  return `${dimension.includeIds?.length || 0} ${noun} اخترتها. الجديد مش هيتضاف لوحده`;
}

export function CatalogPicker({ title, kind, selectedIds, filters, onClose, onConfirm, single }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(() => new Set(selectedIds || []));
  const [labels, setLabels] = useState([]);
  const [categoryId, setCategoryId] = useState(filters?.categoryId || "");
  const [brandId, setBrandId] = useState(filters?.brandId || "");
  const [busy, setBusy] = useState(false);

  const query = useMemo(() => ({
    kind,
    q,
    page,
    pageSize: 24,
    categoryId: categoryId || undefined,
    brandId: brandId || undefined,
    productId: filters?.productId || undefined,
    typeId: filters?.typeId || undefined,
  }), [kind, q, page, categoryId, brandId, filters?.productId, filters?.typeId]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    searchBundleCatalog(query).then((data) => {
      if (cancel) return;
      setRows(data.items || []);
      setTotal(data.total || 0);
      setError("");
    }).catch((err) => {
      if (!cancel) setError(err.message);
    }).finally(() => {
      if (!cancel) setLoading(false);
    });
    return () => { cancel = true; };
  }, [query]);

  useEffect(() => {
    const ids = [...picked].slice(0, 12);
    if (!ids.length) {
      setLabels([]);
      return;
    }
    let cancel = false;
    fetchBundleCatalogLabels(kind, ids).then((data) => {
      if (!cancel) setLabels(data.items || []);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [kind, picked]);

  const pageCount = Math.max(1, Math.ceil(total / 24));
  const visibleIds = rows.map((row) => row.id);
  const allVisible = visibleIds.length > 0 && visibleIds.every((id) => picked.has(id));

  const toggle = (id) => {
    setPicked((current) => {
      const next = new Set(current);
      if (single) return new Set(next.has(id) ? [] : [id]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectMatching = async () => {
    setBusy(true);
    try {
      const data = await fetchBundleCatalogIds({ ...query, page: undefined, pageSize: undefined });
      setPicked((current) => {
        const next = single ? new Set() : new Set(current);
        (data.ids || []).forEach((id) => next.add(id));
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deselectMatching = async () => {
    setBusy(true);
    try {
      const data = await fetchBundleCatalogIds({ ...query, page: undefined, pageSize: undefined });
      const drop = new Set(data.ids || []);
      setPicked((current) => new Set([...current].filter((id) => !drop.has(id))));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-6" dir="rtl">
      <div className="flex max-h-[100dvh] w-full max-w-3xl flex-col rounded-t-2xl bg-white shadow-xl sm:max-h-[85vh] sm:rounded-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-lg font-bold">{title}</h3>
          <button type="button" className="text-slate-500" onClick={onClose}>إغلاق</button>
        </div>
        <div className="space-y-3 overflow-y-auto px-4 py-3">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="بحث بالاسم أو الكود" value={q} onChange={(event) => { setPage(1); setQ(event.target.value); }} />
          <div className="flex flex-wrap gap-2">
            {filters?.categories && (kind === "design" || kind === "product") && (
              <select className="rounded-xl border px-3 py-2" value={categoryId} onChange={(event) => { setPage(1); setCategoryId(event.target.value); }}>
                <option value="">كل الفئات</option>
                {filters.categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            )}
            {filters?.brands && kind === "phone" && (
              <select className="rounded-xl border px-3 py-2" value={brandId} onChange={(event) => { setPage(1); setBrandId(event.target.value); }}>
                <option value="">كل العلامات</option>
                {filters.brands.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            )}
          </div>
          {!single && (
            <div className="flex flex-wrap gap-2 text-sm">
              <button type="button" className="rounded-lg bg-purple-700 px-3 py-1 text-white disabled:opacity-50" disabled={busy || total === 0} onClick={selectMatching}>تحديد كل النتائج المطابقة ({total})</button>
              <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setPicked((current) => new Set([...current, ...visibleIds]))}>{allVisible ? "الصفحة محددة" : "تحديد الظاهر في الصفحة"}</button>
              <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" disabled={busy} onClick={deselectMatching}>إلغاء المطابق للبحث</button>
              <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setPicked(new Set())}>مسح التحديد</button>
            </div>
          )}
          <p className="text-sm text-slate-600">المحدد: {picked.size}{single ? "" : " — التحديد لا يضيع لما تغيّر البحث أو الصفحة"}</p>
          {labels.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {labels.map((item) => (
                <button key={item.id} type="button" className="rounded-full bg-purple-50 px-3 py-1 text-sm text-purple-800" onClick={() => toggle(item.id)}>
                  {item.name} ×
                </button>
              ))}
              {picked.size > labels.length && <span className="text-sm text-slate-500">+ {picked.size - labels.length}</span>}
            </div>
          )}
          {error && <p className="text-red-600">{error}</p>}
          {loading && <p>جاري التحميل...</p>}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {rows.map((row) => (
              <label key={row.id} className={`flex items-center gap-3 rounded-xl border p-2 ${picked.has(row.id) ? "border-purple-500 bg-purple-50" : ""}`}>
                <input type={single ? "radio" : "checkbox"} checked={picked.has(row.id)} onChange={() => toggle(row.id)} />
                {row.imageUrl && <img src={resolveMediaUrl(row.imageUrl)} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{row.name}</span>
                  {row.code && <span className="block text-xs text-slate-500">{row.code}</span>}
                </span>
              </label>
            ))}
          </div>
          {!loading && !error && rows.length === 0 && <p className="text-slate-500">لا توجد نتائج.</p>}
          <div className="flex items-center justify-between">
            <button type="button" className="rounded-lg bg-slate-100 px-3 py-1 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>السابق</button>
            <span className="text-sm">{page} / {pageCount}</span>
            <button type="button" className="rounded-lg bg-slate-100 px-3 py-1 disabled:opacity-40" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>التالي</button>
          </div>
        </div>
        <div className="border-t px-4 py-3">
          <button type="button" className="w-full rounded-xl bg-purple-700 py-3 text-white" onClick={() => onConfirm([...picked])}>تأكيد التحديد</button>
        </div>
      </div>
    </div>
  );
}

export function DimensionEditor({ title, noun, dimension, onChange, kind, ruleKind, filters }) {
  const [picker, setPicker] = useState(null);
  const value = dimension || emptyDimension();
  const patch = (next) => onChange({ ...value, ...next });
  const modes = ruleKind ? ["None", "All", "Rules", "Manual"] : ["None", "All", "Manual"];

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{title}</p>
        <span className="text-sm text-slate-500">{summary(value, noun, ruleKind)}</span>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        {modes.map((mode) => (
          <button key={mode} type="button" className={`rounded-full px-3 py-1 text-sm ${value.mode === mode ? "bg-purple-700 text-white" : "bg-slate-100"}`} onClick={() => patch({ mode })}>
            {mode === "Rules" ? (ruleKind === "brand" ? "حسب الماركة" : "حسب الفئة") : MODE_LABELS[mode]}
          </button>
        ))}
      </div>
      {value.mode === "Rules" && ruleKind && (
        <div className="mb-2 flex flex-wrap gap-2">
          <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setPicker("groups")}>
            {ruleKind === "brand" ? `الماركات (${value.brandIds.length})` : `الفئات (${value.categoryIds.length})`}
          </button>
          <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setPicker("include")}>زود قطع معينة ({value.includeIds.length})</button>
        </div>
      )}
      {(value.mode === "All" || value.mode === "Rules") && (
        <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setPicker("exclude")}>استبعد بعض القطع ({value.excludeIds.length})</button>
      )}
      {value.mode === "Manual" && (
        <button type="button" className="rounded-lg bg-purple-50 px-3 py-1 text-purple-800" onClick={() => setPicker("include")}>اختار القطع ({value.includeIds.length})</button>
      )}
      {picker && (
        <CatalogPicker
          title={picker === "groups" ? (ruleKind === "brand" ? "الماركات" : "الفئات") : title}
          kind={picker === "groups" ? ruleKind : kind}
          single={false}
          filters={picker === "groups" ? null : filters}
          selectedIds={picker === "groups" ? (ruleKind === "brand" ? value.brandIds : value.categoryIds) : picker === "exclude" ? value.excludeIds : value.includeIds}
          onClose={() => setPicker(null)}
          onConfirm={(ids) => {
            if (picker === "groups") patch(ruleKind === "brand" ? { brandIds: ids } : { categoryIds: ids });
            else if (picker === "exclude") patch({ excludeIds: ids });
            else patch({ includeIds: ids });
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}

export function SinglePickerButton({ label, kind, selectedId, filters, onChange }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => {
    if (!selectedId) {
      setName("");
      return;
    }
    fetchBundleCatalogLabels(kind, [Number(selectedId)]).then((data) => {
      setName(data.items?.[0]?.name || `#${selectedId}`);
    }).catch(() => setName(`#${selectedId}`));
  }, [kind, selectedId]);

  return (
    <>
      <button type="button" className="w-full rounded-lg border px-3 py-2 text-right" onClick={() => setOpen(true)}>
        {label}: {name || "اختَر"}
      </button>
      {open && (
        <CatalogPicker
          title={label}
          kind={kind}
          single
          filters={filters}
          selectedIds={selectedId ? [Number(selectedId)] : []}
          onClose={() => setOpen(false)}
          onConfirm={(ids) => {
            onChange(ids[0] || null);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
